<template>
	<div class="flex items-center rounded border border-neutral-400 focus-within:ring-1 focus-within:!ring-primary focus-within:!border-primary"
	     :class="{ '!border-red-500 !ring-red-500': invalid, [wrapperClass ?? '']: true }">
		<slot name="prefix"><span v-if="prefix" class="pl-3 opacity-60 select-none whitespace-nowrap">{{ prefix }}</span></slot>
		<input
			ref="inputEl"
			class="focus:outline-none text-base flex items-center w-full py-2.5 px-3.5 bg-white rounded"
			:class="{ 'bg-disabled': disabled, [inputClass ?? '']: true }"
			type="text"
			inputmode="text"
			v-model="displayText"
			:name="name"
			:id="id"
			:placeholder="placeholder"
			:disabled="disabled"
			:data-disabled="disabled"
			:readonly="readonly"
			@focus="onFocus"
			@input="onInput"
			@blur="commit"
			@keyup.enter="commit"
			@keyup="emit('keyup', $event)"
		>
		<slot name="suffix">
			<span v-if="suffix ?? unit" class="pr-3 opacity-60 select-none whitespace-nowrap">{{ suffix ?? unit }}</span>
		</slot>
	</div>
</template>

<script setup lang="ts">
import { evaluateExpression } from "../utils/evaluateExpression";
import { formatNumberLocale } from "../utils/NumberFormatter";

const props = withDefaults(
	defineProps<{
		id?: string;
		name?: string;
		step?: string | number;
		disabled?: boolean;
		readonly?: boolean;
		unit?: string;
		placeholder?: string;
		prefix?: string;
		suffix?: string;
		inputClass?: string;
		wrapperClass?: string;
		invalid?: boolean;
		/**
		 * When > 0, the typed value is also committed this many ms after the last keystroke, instead
		 * of only on blur/Enter, so dependants (AppQuantities' recalculation) can follow along while
		 * the user is still typing. The displayed text is left untouched until the edit finishes
		 * either way.
		 */
		liveDebounce?: number;
	}>(),
	{
		disabled: false,
		readonly: false,
		invalid: false,
		liveDebounce: 0,
	},
);

const emit = defineEmits<{
	(e: "change", value: number | null): void;
	(e: "keyup", event: KeyboardEvent): void;
	(e: "focus", event: FocusEvent): void;
	(e: "blur", event: FocusEvent): void;
}>();

// Loosely typed to match the untyped v-model refs the host components bind
// (e.g. AppCustomInput's shared model, AppSchemaInput's schema value). The
// emitted `change` value is strongly typed as `number | null`.
const model = defineModel<any>();

const inputEl = ref<HTMLInputElement | null>(null);
const displayText = ref("");

// Set from the first keystroke until the edit is committed. While it holds, an external model
// change must not rewrite `displayText`: that would move the caret and can throw away a
// half-typed expression.
const isEditing = ref(false);
let pendingCommit: ReturnType<typeof setTimeout> | null = null;

// Number of decimals to show in the formatted result, derived from `step`.
const maxFractionDigits = computed(() => {
	const step = props.step;
	if (step == null || step === "" || step === "any") return 6;
	const normalized = step.toString().replace(",", ".");
	const dot = normalized.indexOf(".");
	return dot === -1 ? 0 : normalized.length - dot - 1;
});

function format(value: number | string | null | undefined): string {
	if (value == null || value === "") return "";
	const asNumber = typeof value === "string" ? parseFloat(value.replace(",", ".")) : value;
	if (Number.isNaN(asNumber)) return "";
	return formatNumberLocale(asNumber, maxFractionDigits.value);
}

// Reflect external model changes into the display, but never on top of an edit in progress.
watch(
	model,
	(value) => {
		if (isEditing.value) return;
		displayText.value = format(value);
	},
	{ immediate: true },
);

function onFocus(event: FocusEvent) {
	emit("focus", event);
}

function cancelPendingCommit() {
	if (pendingCommit == null) return;
	clearTimeout(pendingCommit);
	pendingCommit = null;
}

onBeforeUnmount(cancelPendingCommit);

function onInput() {
	isEditing.value = true;

	if (props.liveDebounce <= 0) return;

	cancelPendingCommit();
	pendingCommit = setTimeout(() => {
		pendingCommit = null;
		// `isEditing` deliberately stays set: only the value is published early, the text the user
		// is still working on stays as typed.
		applyValue(false);
	}, props.liveDebounce);
}

function commit(event: Event) {
	if (event.type === "blur") emit("blur", event as FocusEvent);

	cancelPendingCommit();
	isEditing.value = false;
	applyValue(true);
}

/**
 * Parse the typed text and push it into the model.
 *
 * `normalizeDisplay` is only true once the edit is finished (blur/Enter). While the user is still
 * typing the text is left exactly as typed: reformatting would move the caret, and a half-typed
 * expression such as `5*` must not be reverted to the previous value mid-keystroke.
 */
function applyValue(normalizeDisplay: boolean) {
	const raw = displayText.value.trim();

	if (raw === "") {
		if (model.value != null && model.value !== "") {
			model.value = null;
			emit("change", null);
		}
		if (normalizeDisplay) displayText.value = "";
		return;
	}

	try {
		const result = evaluateExpression(raw);
		if (result == null) {
			model.value = null;
			if (normalizeDisplay) displayText.value = "";
			emit("change", null);
			return;
		}
		const numeric = result.toNumber();
		if (model.value == null || Number(model.value) !== numeric) {
			model.value = numeric;
			emit("change", numeric);
		}
		if (normalizeDisplay) displayText.value = format(numeric);
	} catch {
		// Invalid or still-incomplete expression: leave the model on its last committed value. Only
		// a finished edit reverts the text to it; while typing the user keeps what they have.
		if (normalizeDisplay) displayText.value = format(model.value);
	}
}
</script>
